import type { CpqState, Quote, QuoteContainerInstance } from './types';

const same = (left: unknown, right: unknown) =>
  JSON.stringify(left) === JSON.stringify(right);

/** A new source child may attach only to an existing quote plan with an exact
 * catalog/local recipe. This never authorizes moving an existing source row. */
export function quoteNewSourceChildChain(
  state: CpqState,
  previous: Quote,
  next: Quote,
  childId: string,
): QuoteContainerInstance[] | undefined {
  if (
    previous.containerEditMode !== 'quote_edit' ||
    next.containerEditMode !== 'quote_edit' ||
    previous.containerInstances?.some((instance) => instance.id === childId)
  )
    return undefined;
  const child = next.containerInstances?.find(
    (instance) => instance.id === childId && instance.kind === 'package',
  );
  const parent = next.containerInstances?.find(
    (instance) =>
      instance.id === child?.parentInstanceId && instance.kind === 'plan',
  );
  const original = previous.containerInstances?.find(
    (instance) => instance.id === parent?.id && instance.kind === 'plan',
  );
  if (
    !child ||
    !parent ||
    !original ||
    parent.parentInstanceId !== null ||
    child.status !== 'customized' ||
    parent.status !== 'customized' ||
    !child.parentMemberId ||
    !Number.isSafeInteger(parent.quantity) ||
    parent.quantity < 1 ||
    !Number.isSafeInteger(child.quantity) ||
    child.quantity < 1 ||
    child.quantity % parent.quantity !== 0 ||
    parent.quantity !== original.quantity ||
    !same(
      [
        parent.kind,
        parent.originKind,
        parent.originId,
        parent.originRevisionId,
        parent.originResourceVersion,
        parent.parentInstanceId,
        parent.parentMemberId,
        parent.originSnapshot,
      ],
      [
        original.kind,
        original.originKind,
        original.originId,
        original.originRevisionId,
        original.originResourceVersion,
        original.parentInstanceId,
        original.parentMemberId,
        original.originSnapshot,
      ],
    ) ||
    next.containerInstances?.filter(
      (instance) =>
        instance.parentInstanceId === parent.id &&
        instance.parentMemberId === child.parentMemberId,
    ).length !== 1
  )
    return undefined;
  const groups =
    next.quoteGroups?.filter(
      (group) => group.containerInstanceId === child.id,
    ) ?? [];
  if (groups.length !== 1) return undefined;
  const group = groups[0];
  const rows = next.items.filter((row) => row.containerInstanceId === child.id);
  if (
    !rows.length ||
    rows.some(
      (row) =>
        row.quoteGroupId !== group.id ||
        previous.items.some((originalRow) => originalRow.itemId === row.itemId),
    )
  )
    return undefined;
  const members = child.originSnapshot.members;
  if (
    !members.length ||
    members.length !== rows.length ||
    members.some(
      (member) =>
        member.sourceKind !== 'equipment' ||
        !member.sourceRevisionId ||
        !Number.isSafeInteger(member.quantity) ||
        member.quantity < 1,
    )
  )
    return undefined;
  if (child.originKind === 'catalog-package') {
    const pkg = state.catalog.packages.find(
      (candidate) =>
        candidate.id === child.originId &&
        candidate.status === 'active' &&
        candidate.currentRevisionId === child.originRevisionId,
    );
    if (
      !pkg ||
      group.kind !== 'display' ||
      group.definitionId != null ||
      child.originResourceVersion != null ||
      (child.originSnapshot.name !== undefined &&
        child.originSnapshot.name !== pkg.name) ||
      child.originSnapshot.revisionId !== pkg.currentRevisionId ||
      child.originSnapshot.resourceVersion !== null ||
      pkg.productLinks.length !== members.length ||
      new Set(members.map((member) => member.memberId)).size !==
        members.length ||
      members.some(
        (member) =>
          !pkg.productLinks.some(
            (link) =>
              link.relationId === member.memberId &&
              link.deviceId === member.sourceId &&
              link.deviceRevisionId === member.sourceRevisionId &&
              link.quantity === member.quantity,
          ),
      )
    )
      return undefined;
    if (
      rows.some(
        (row) =>
          row.sourceType !== 'package' ||
          row.sourceId !== pkg.id ||
          row.sourceRevisionId !== pkg.currentRevisionId ||
          row.packageId !== pkg.id ||
          !members.some(
            (member) =>
              member.memberId === row.packageRelationId &&
              member.sourceId === row.deviceId &&
              member.sourceRevisionId ===
                row.deviceFieldSnapshot?.deviceRevisionId &&
              row.packageRelationQuantitySnapshot === member.quantity &&
              row.quantity === member.quantity * child.quantity,
          ),
      )
    )
      return undefined;
  } else if (child.originKind === 'quote-local-package') {
    const definition = next.localPackageDefinitions?.find(
      (candidate) =>
        candidate.id === child.originId &&
        candidate.resourceVersion === child.originResourceVersion,
    );
    const previousDefinition = previous.localPackageDefinitions?.find(
      (candidate) => candidate.id === child.originId,
    );
    if (
      !definition ||
      (previousDefinition && !same(previousDefinition, definition)) ||
      (child.originSnapshot.name !== undefined &&
        child.originSnapshot.name !== definition.name) ||
      child.originRevisionId !== null ||
      child.originSnapshot.revisionId !== null ||
      child.originSnapshot.resourceVersion !== definition.resourceVersion ||
      group.kind !== 'quote-local-package' ||
      group.definitionId !== definition.id ||
      !same(group.members, definition.members) ||
      definition.members.length !== members.length ||
      new Set(members.map((member) => member.sourceId)).size !==
        members.length ||
      members.some(
        (member) =>
          !definition.members.some(
            (entry) =>
              entry.deviceId === member.sourceId &&
              entry.quantity === member.quantity,
          ),
      ) ||
      rows.some(
        (row) =>
          row.sourceType !== 'device' ||
          row.sourceId !== row.deviceId ||
          row.packageId !== undefined ||
          row.packageRelationId !== undefined ||
          !members.some(
            (member) =>
              member.sourceId === row.deviceId &&
              row.sourceRevisionId === member.sourceRevisionId &&
              row.deviceFieldSnapshot?.deviceRevisionId ===
                member.sourceRevisionId &&
              row.packageRelationQuantitySnapshot === undefined &&
              Number.isSafeInteger(row.quantity / child.quantity) &&
              row.quantity / child.quantity === member.quantity &&
              row.quantity === member.quantity * child.quantity,
          ),
      )
    )
      return undefined;
  } else return undefined;
  return [child, parent];
}
